import { useQuery } from '@apollo/client';
import * as React from 'react';

import Error from './../../components/layout/Error';
import Loading from './../../components/layout/Loading';

export interface ISorting {
    field: string;
    direction: string;
}

export interface IGraphQLListProps {
    offset: number;
    limit: number;
    filter?: any;
    sortBy?: ISorting;
    fetchPolicy?: any;
    query?: any;
    variables?: any;
    onResult?: (returnData: any) => void;
    onResultDataProp?: string;
    hideLoadingIndicator?: boolean;
}

interface IProps extends IGraphQLListProps {
    dataProp: string;
    query: any;
    renderFetchedList: (
        fetchedData: any[],
        totalCount: number,
        limit: number,
        offset: number,
    ) => JSX.Element;
    children?: any;
}

const ListQueryContainer: React.FC<IProps> = ({
    renderFetchedList,
    offset,
    limit,
    sortBy,
    query,
    dataProp,
    fetchPolicy,
    filter,
    variables,
    hideLoadingIndicator,
    onResult,
    onResultDataProp,
}) => {
    const queryVariables: any = { ...variables, offset, limit };

    if (sortBy) {
        queryVariables.order = { by: sortBy.field, direction: sortBy.direction };
    }

    if (filter) {
        queryVariables.filter = filter;
    }

    const { data, loading, error } = useQuery(query, {
        variables: queryVariables,
        fetchPolicy: fetchPolicy || 'network-only',
    });

    React.useEffect(() => {
        if (data && onResult) {
            const requestedData = onResultDataProp
                ? data[dataProp][onResultDataProp]
                : data[dataProp];
            onResult(requestedData);
        }
    }, [data, onResult, dataProp, onResultDataProp]);

    if (loading && !(data && data[dataProp])) {
        return !hideLoadingIndicator && <Loading />;
    }
    if (error) {
        return <Error>Error fetching data...</Error>;
    }

    if (!data || !data[dataProp]) {
        return null;
    }

    const {
        data: fetchedData,
        totalCount,
        limit: fetchedLimit,
        offset: fetchedOffset,
    }: {
        data: any[];
        totalCount: number;
        limit: number;
        offset: number;
    } = data[dataProp];

    return renderFetchedList(fetchedData, totalCount, fetchedLimit, fetchedOffset);
};

export default ListQueryContainer;
